import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { fetchWalletPositions } from '../zerionApi';
import { exportCSV } from '../utils/exportData';
import { useWallet } from '../contexts/WalletContext';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProGate from './ProGate';
import { HiOutlineDocumentDownload } from 'react-icons/hi';

function fmtUsd(v) {
  if (v == null || isNaN(v)) return '$0.00';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function PositionReportView() {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const { account, isConnected } = useWallet();

  const [address, setAddress] = useState('');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);

  const targetAddress = address || account;

  const analyze = useCallback(async () => {
    if (!targetAddress || !/^0x[0-9a-fA-F]{40}$/.test(targetAddress)) {
      toast.error('Invalid wallet address');
      return;
    }
    setLoading(true);
    try {
      const pos = await fetchWalletPositions(targetAddress);
      setPositions(pos || []);
      setAnalyzed(true);
    } catch (err) {
      toast.error('Report generation failed. Check that the API key is configured.');
    }
    setLoading(false);
  }, [targetAddress]);

  const totalValue = useMemo(() =>
    positions.reduce((s, p) => s + (p.totalValueUsd || 0), 0),
    [positions]
  );

  const totalPnl = useMemo(() =>
    positions.reduce((s, p) => s + (p.changes?.absolute_1d || 0), 0),
    [positions]
  );

  const handleExportPDF = async () => {
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');

      const element = document.getElementById('report-content');
      if (!element) return;

      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`poolscope-report-${targetAddress?.slice(0, 8)}.pdf`);
      toast.success('PDF exported');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('PDF export failed');
    }
  };

  const handleExportCSV = () => {
    const data = positions.map(p => ({
      protocol: p.protocolName,
      pool: p.poolName,
      chain: p.chain,
      type: p.type,
      value: p.totalValueUsd?.toFixed(2),
      change_1d: p.changes?.absolute_1d?.toFixed(2),
      change_1d_pct: p.changes?.percent_1d?.toFixed(2),
      tokens: p.tokens?.map(t => `${t.symbol}: ${t.value?.toFixed(2)}`).join(', '),
    }));
    exportCSV(data, `poolscope-report-${targetAddress?.slice(0, 8)}`);
    toast.success('CSV exported');
  };

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: isMobile ? 24 : 28,
            color: colors.textPrimary,
            marginBottom: 4,
          }}>
            Position Report
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.textSecondary,
          }}>
            Detailed performance report with export to PDF/CSV
          </div>
        </div>
        {analyzed && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleExportCSV}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: colors.textSecondary,
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                borderRadius: 8,
                padding: '7px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <HiOutlineDocumentDownload size={14} />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                color: '#FFFFFF',
                background: '#B8001A',
                border: 'none',
                borderRadius: 8,
                padding: '7px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <HiOutlineDocumentDownload size={14} />
              PDF
            </button>
          </div>
        )}
      </div>

      {/* Search bar */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
      }}>
        <input
          type="text"
          value={address}
          onChange={e => setAddress(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && analyze()}
          placeholder={isConnected ? `Connected: ${account?.slice(0, 8)}...` : 'Enter wallet address (0x...)'}
          style={{
            flex: 1,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: colors.textPrimary,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 10,
            padding: '12px 16px',
            outline: 'none',
          }}
        />
        <button
          onClick={analyze}
          disabled={loading}
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: '#FFFFFF',
            background: colors.green,
            border: 'none',
            borderRadius: 10,
            padding: '0 20px',
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {loading && (
        <div style={{
          textAlign: 'center',
          padding: 40,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.labelMuted,
        }}>
          Generating performance report...
        </div>
      )}

      {analyzed && !loading && (
        <div id="report-content">
          {/* Summary cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
            gap: 12,
            marginBottom: 20,
          }}>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                PORTFOLIO VALUE
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: colors.green }}>
                {fmtUsd(totalValue)}
              </div>
            </div>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                24H P&L
              </div>
              <div style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 26,
                color: totalPnl >= 0 ? colors.green : '#B8001A',
              }}>
                {totalPnl >= 0 ? '+' : ''}{fmtUsd(totalPnl)}
              </div>
            </div>
            <div style={{
              background: colors.surface,
              borderRadius: 10,
              border: `1px solid ${colors.border}`,
              padding: 16,
            }}>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelLight, letterSpacing: 1, marginBottom: 6 }}>
                POSITIONS
              </div>
              <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: colors.textPrimary }}>
                {positions.length}
              </div>
            </div>
          </div>

          {/* Positions detail */}
          <div style={{
            background: colors.surface,
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: 20,
          }}>
            <div style={{
              padding: '12px 16px',
              background: dark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
              borderBottom: `1px solid ${colors.border}`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              fontWeight: 600,
              color: colors.labelLight,
              letterSpacing: 1,
            }}>
              POSITION BREAKDOWN
            </div>
            {positions.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: colors.labelMuted }}>
                No positions found
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: dark ? 'rgba(255,255,255,0.02)' : '#FAFAFA' }}>
                      {['POSITION', 'PROTOCOL', 'CHAIN', 'VALUE', '24H CHANGE'].map(h => (
                        <th key={h} style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 9,
                          fontWeight: 600,
                          color: colors.labelLight,
                          letterSpacing: 1.2,
                          textAlign: 'left',
                          padding: '8px 14px',
                          borderBottom: `1px solid ${colors.border}`,
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((pos, i) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#F4F5F6'}` }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: colors.textPrimary, fontWeight: 500 }}>
                            {pos.poolName || 'Position'}
                          </div>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted, marginTop: 2 }}>
                            {pos.tokens?.map(t => t.symbol).join(' / ')}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>
                          {pos.protocolName}
                        </td>
                        <td style={{ padding: '12px 14px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: colors.textSecondary }}>
                          {pos.chain}
                        </td>
                        <td style={{ padding: '12px 14px', fontFamily: "'DM Serif Display', serif", fontSize: 15, color: colors.textPrimary }}>
                          {fmtUsd(pos.totalValueUsd)}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {pos.changes?.absolute_1d != null && (
                            <div style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 12,
                              color: pos.changes.absolute_1d >= 0 ? colors.green : '#B8001A',
                            }}>
                              {pos.changes.absolute_1d >= 0 ? '+' : ''}{fmtUsd(pos.changes.absolute_1d)}
                              <span style={{ fontSize: 10, marginLeft: 4 }}>
                                ({pos.changes.percent_1d >= 0 ? '+' : ''}{pos.changes.percent_1d?.toFixed(2)}%)
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Tax section */}
          <div style={{
            background: colors.surface,
            borderRadius: 12,
            border: `1px solid ${colors.border}`,
            padding: 20,
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              fontWeight: 600,
              color: colors.labelLight,
              letterSpacing: 1,
              marginBottom: 12,
            }}>
              TAX SUMMARY (ESTIMATED)
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
              gap: 12,
            }}>
              <div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted, marginBottom: 4 }}>
                  24H Change
                </div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: totalPnl >= 0 ? colors.green : '#B8001A' }}>
                  {totalPnl >= 0 ? '+' : ''}{fmtUsd(totalPnl)}
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted, marginBottom: 4 }}>
                  Realized Gains
                </div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: colors.textSecondary }}>
                  N/A
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: colors.labelMuted, marginBottom: 4 }}>
                  Cost Basis
                </div>
                <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18, color: colors.textSecondary }}>
                  N/A
                </div>
              </div>
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
              marginTop: 12,
            }}>
              * Tax calculations are estimates. Consult a tax professional for accurate reporting.
            </div>
          </div>

          {/* Report footer */}
          <div style={{
            marginTop: 16,
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelMuted,
            textAlign: 'center',
          }}>
            Report generated {new Date().toLocaleString()} &middot; PoolScope Pro
          </div>
        </div>
      )}
    </motion.div>
  );

  return (
    <ProGate feature="position reports with PDF export">
      {content}
    </ProGate>
  );
}
